import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useIngestStatus } from '../../hooks/useIssueIngest.js';
import { useBacklogSync } from '../../hooks/useIssueTodo.js';

/**
 * 異常の帯1本（何が問題か → 推奨アクション）。
 * @graph-connects none
 */
function Band({ message, action }: { message: string; action: string }) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-error)] bg-[color-mix(in_srgb,var(--color-error)_8%,transparent)] px-4 py-2.5 text-sm text-[var(--color-text-primary)]"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-[var(--color-error)]" aria-hidden="true" />
      <span className="min-w-0 flex-1">{message}</span>
      <Link
        to="/issue-mgmt/settings"
        className="text-sm font-medium text-[var(--color-accent-primary)] hover:underline"
      >
        {action}
      </Link>
    </div>
  );
}

/**
 * 課題タブとやることの上に出す異常の帯。Slack の保存（ai-fassy）が24時間より古いとき、取込みが止まったチャンネル
 * （ai-fassy の招待待ち・続けて失敗）と、Backlog の写しが26時間より古いときだけ出し、どれも設定タブへ案内する。
 * 問題が無ければ何も出さない。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Slack の保存（ai-fassy）が止まっていること・取込みが止まったチャンネル・Backlog の写しが古いことを赤い帯で知らせ、設定タブで直すよう案内する
 * @graph-connects useIngestStatus [calls] 取込みチャンネルの状態と Slack の保存の状況
 * @graph-connects useBacklogSync [calls] Backlog の写しの鮮度
 */
export function AnomalyBands() {
  const { t } = useTranslation();
  const ingest = useIngestStatus();
  const backlog = useBacklogSync();
  const stopped = (ingest.data?.channels ?? []).filter(
    (channel) => channel.state === 'stopped' || channel.state === 'awaiting_invite',
  );
  const archiveStale = ingest.data?.slackArchive?.stale === true;
  const stale = backlog.data?.mirrorStale === true;
  const staleHours = backlog.data?.mirrorStaleHours ?? null;
  if (stopped.length === 0 && !stale && !archiveStale) return null;
  return (
    <div className="mb-4 space-y-2">
      {archiveStale && (
        <Band
          message={t('issueMgmt.anomaly.archiveStopped')}
          action={t('issueMgmt.anomaly.openSettings')}
        />
      )}
      {stopped.map((channel) => (
        <Band
          key={channel.channelId}
          message={
            channel.state === 'awaiting_invite'
              ? t('issueMgmt.anomaly.awaitingInvite', { channel: channel.channelName })
              : t('issueMgmt.anomaly.ingestStopped', { channel: channel.channelName })
          }
          action={t('issueMgmt.anomaly.openSettings')}
        />
      ))}
      {stale && (
        <Band
          message={
            staleHours === null
              ? t('issueMgmt.anomaly.backlogNeverSynced')
              : t('issueMgmt.anomaly.backlogStale', { hours: staleHours })
          }
          action={t('issueMgmt.anomaly.openSettings')}
        />
      )}
    </div>
  );
}
